import { GraphQLCampaign } from 'modules/campaigns/lib';
import {
  CampaignAlertDetails,
  DisabledAlerts,
} from 'modules/campaigns/lib/components/CampaignAlertDetails';
import { getIsTriggeredCampaignType } from 'modules/campaigns/lib/utils';
import { CampaignState } from 'modules/domains/campaign';
import { useState } from 'react';
import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import { Toggle } from 'ui/toggle';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import { NO_PROJECT_ALERTS_TOOLTIP_CONTENT } from '../../constants';
import { useCampaignAlertsContext, useCampaignContext } from '../../context';
import { useGenerateCampaignAlerts, useToggleAlerts } from '../../hooks';

const Wrapper = styled.div`
  background: ${colors.backgroundSecondary};
  display: flex;
  margin: -${spacing.plusHalf} 0 ${spacing.plusHalf} -${spacing.plusHalf};
  padding-left: ${spacing.plusHalf};
  width: calc(100% + ${spacing.x3});
`;

const StyledTooltip = styled(Tooltip)`
  height: fit-content;
`;

const StyledToggle = styled(Toggle)`
  padding-top: ${pxToRem(18)};
`;

export const CAMPAIGN_SUMMARY_ALERTS_ID = 'campaign-summary-alerts';
export const CAMPAIGN_SUMMARY_ALERTS_TOGGLE_ID = 'campaign-summary-alerts-toggle';

export const getShouldGenerateAlerts = (campaign: GraphQLCampaign) =>
  getIsTriggeredCampaignType(campaign.campaignTypeDefinition) &&
  campaign.campaignState === CampaignState.Running;

export const CampaignSummaryAlerts = () => {
  const { campaign } = useCampaignContext();
  const { alertDetails, hasProjectAlerts } = useCampaignAlertsContext();
  const { generateCampaignAlerts } = useGenerateCampaignAlerts();

  const isDisabled = !hasProjectAlerts;
  const { onTakeoverOpen } = useCampaignAlertsContext();

  const [useCampaignAlerts, setUseCampaignAlerts] = useState(!!campaign?.campaignAlertsEnabled);

  const { toggleAlerts } = useToggleAlerts({
    onCompleted: (data) => {
      const { campaignAlertsEnabled } = data.updateCampaign;

      if (getShouldGenerateAlerts(campaign)) {
        generateCampaignAlerts({ campaignAlertsEnabled, id: campaign.id });
      }
    },
    onError: () => {
      setUseCampaignAlerts((value) => !value); // state shouldn't be saved so reset it to previous value
    },
  });

  return (
    <Wrapper data-test={CAMPAIGN_SUMMARY_ALERTS_ID}>
      <StyledTooltip
        isEnabled={isDisabled}
        tooltipContent={NO_PROJECT_ALERTS_TOOLTIP_CONTENT}
        placement={TipPlacement.Right}
      >
        <StyledToggle
          data-test={CAMPAIGN_SUMMARY_ALERTS_TOGGLE_ID}
          isActive={useCampaignAlerts}
          isDisabled={isDisabled}
          onClick={() => {
            setUseCampaignAlerts((value) => {
              const newValue = !value;
              toggleAlerts(newValue);
              return newValue;
            });
          }}
        />
      </StyledTooltip>

      {useCampaignAlerts ? (
        <CampaignAlertDetails details={alertDetails} onTakeoverOpen={onTakeoverOpen} />
      ) : (
        <DisabledAlerts />
      )}
    </Wrapper>
  );
};
